import {
  Component,
  HostListener,
  AfterViewChecked,
  AfterViewInit,
  ElementRef,
  ViewChild,
  QueryList,
  ViewChildren,
  OnInit,
  OnDestroy,
  Renderer2,
  Inject,
} from '@angular/core';
import { firstValueFrom, take } from 'rxjs';
import { NgbOffcanvas, NgbOffcanvasOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { TuiAlertService } from '@taiga-ui/core';
import { ServicesService } from 'src/app/services/service/services.service';
import { Zone, ZonesResponse } from 'src/app/models/zone/zone.model';
import {
  DriverGant,
  GanttDataItem,
  GanttRequest,
  GanttTask,
  TimeData,
} from './interfaces/Gant.interface';
import {
  NoProviderService,
  NoProviderServiceGetPayload,
  NoProviderServiceGetResponse,
} from './interfaces/NoProviderService.model';
import { Verticals } from 'src/app/models/service/service.model';
import { vehicleChasisTypeWithTranslations } from 'src/app/constants/data';
import { formatSecondsToHourAndMinutes } from 'src/app/utils';
import { GanttSidebarComponent } from './components/gantt-sidebar/gantt-sidebar.component';
import { SessionStorageService } from 'src/app/services/session-storage/session-storage.service';
import { SessionStorageKeys } from 'src/app/enum/session-storage.enum';
import { ServicesWithoutProviderSideComponent } from '../services-without-provider-side/services-without-provider-side.component';
import { isValidGuid } from 'src/app/utils/guid-validator';
import { DriverType, DriverTypeLabel, DriverTypeApiValue, DriverTypeApiLabel } from 'src/app/enum/driver-type.enum';
import { NodeTypeApiValue } from 'src/app/enum/node-type.enum';
import { ExcelService } from 'src/app/services/excel/excel.service';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import {
  ServiceTypeMobility,
  ServiceTypeMobilityLabelKey,
} from 'src/app/enum/service-type-mobility.enum';

declare const moment: any;
moment.locale('es');

const MIN_SEC = 5 * 60;
const DEF_SEC = 30 * 60;
const NODE_H = 55;
const GAP_Y = 5;

const GANTT_START_H = 7;
const GANTT_END_H = 20;
const CURRENT_TIME_REFRESH_MS = 15_000;

@Component({
  selector: 'app-gantt',
  templateUrl: './gantt.component.html',
  styleUrls: ['./gantt.component.css'],
})
export class GanttComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy
{
  ganttStartTime = GANTT_START_H;
  ganttEndTime = GANTT_END_H;
  halfHourMode = false;
  rangeWidth = 100;

  actualDate: string = moment().format('D [de] MMMM [de] YYYY');
  startTimeLabel = '1:00';
  searchTerm = '';
  selectedZone: string | undefined;
  selectedDate: Date | null = new Date();
  loadingGantt = false;
  isLoading = false;
  isFullscreen = false;
  noMatches = false;
  readonly DriverTypeApiValue = DriverTypeApiValue;
  readonly NodeTypeApiValue = NodeTypeApiValue;
  readonly ServiceTypeMobility = ServiceTypeMobility;

  isCopec: boolean = false;

  // Data
  verticals: Verticals[] = [];
  zones: Zone[] = [];
  ganttData: GanttDataItem[] = [];
  ganttDataOriginal: GanttDataItem[] = [];
  NoProviderServices: NoProviderService[] = [];
  operatorTypes: { label: string; value: string | undefined }[] = [];
  selectedOperatorType: string | null = null;
  selectedIsInternalEmployee: boolean | null = null;
  selectedDriverType: DriverType | null = null;

  operatorEmployeeOptions: { labelKey: string; value: boolean | null }[] = [
    { labelKey: 'service_manager.gantt.operator_type_none', value: null },
    { labelKey: 'service_manager.gantt.operator_type_internal', value: true },
    { labelKey: 'service_manager.gantt.operator_type_external', value: false },
  ];

  driverTypeOptions: { labelKey: string; value: DriverType | null }[] = [
    { labelKey: 'service_manager.gantt.driver_type_all', value: null },
    ...Object.values(DriverType)
      .filter((value): value is DriverType => typeof value === 'number')
      .map((value) => ({ labelKey: DriverTypeLabel[value], value })),
  ];

  // Hours header
  hours: string[] = this.getHoursOfDay(
    this.ganttStartTime,
    this.ganttEndTime,
    this.halfHourMode
  );

  // Timeline
  timelineWidth = 0;
  currentLeftPx = 0;

  isValidGuid = isValidGuid;
  downloadingRows = new Set<string>();

  // Refs
  @ViewChildren('div1') driverArray!: QueryList<ElementRef>;
  @ViewChildren('div2') servicesArray!: QueryList<ElementRef>;
  @ViewChild('father') father!: ElementRef<HTMLDivElement>;

  // Misc
  loader: HTMLElement | null = null;
  private readonly taskTier = new WeakMap<GanttTask, number>();
  private timeTickerId?: number;

  // Interval selector
  intervalOptions = [
    { labelKey: 'service_manager.gantt.half_hour', value: true },
    { labelKey: 'service_manager.gantt.hour', value: false },
  ];

  selectedInterval = this.intervalOptions[1];

  private readonly ICON_BASE_PATH = '../../../assets/icons/gantt-icons/';
  private readonly DEFAULT_ICON = 'Type=Pide_lo_que_necesites.svg';

  private readonly NODE_ICON_BY_TYPE: Record<string, string> = {
    SWEEPER_NODE: 'Type=initial_trans.svg',
    TRANSPORT_NODE: 'Type=tren_trans.svg',
    VEHICLE_NODE: 'Type=public_trans.svg',
  };

  private readonly OPERATIVE_ICON_BY_VERTICAL: Record<string, string> = {
    TRANSFER: 'Type=Transfer.svg',
    MECHANICAL_INSPECTION: 'Type=Falta.svg',
    VEHICLE_WASH: 'Type=Lavados.svg',
    REFUELING: 'Type=Repostaje.svg',
    PRE_TECHNICAL_INSPECTION: 'Type=Pre_ITV.svg',
    LONG_DISTANCE_TRANSFER: 'Type=Media_y_larga_distancia.svg',
    TECHNICAL_INSPECTION: 'Type=ITV.svg',
    VEHICLE_INSURANCE: 'Type=Seguros.svg',
    ACCESSORY: 'Type=Accesorio.svg',
    VALET: 'Type=Valet.svg',
    TYRES: 'Type=Neumaticos.svg',
    FORMALITIES: 'Type=Tramites.svg',
    PARKING: 'Type=Parking.svg',
    REPLACEMENT_VEHICLE: 'Type=Vehiculo_sustitucion.svg',
    TOW_TRUCK: 'Type=Grua.svg',
    WHATEVER_YOU_WANT: 'Type=Pide_lo_que_necesites.svg',
  };

  constructor(
    private servicesSvc: ServicesService,
    private renderer: Renderer2,
    private sessionStorageService: SessionStorageService,
    private offcanvasService: NgbOffcanvas,
    private translate: TranslateService,
    private excelSvc: ExcelService,
    @Inject(TuiAlertService) private readonly alerts: TuiAlertService,
    private permissionsService: PermissionsService,
  ) {}

  ngOnInit(): void {
    this.loader = document.getElementById('loader');

    this.loadGant();
    this.translate.onLangChange.subscribe(() => this.updateOperatorTypes());
    this.handleToggleFullscreen();

    this.permissionsService
      .isCopec()
      .pipe(take(1))
      .subscribe((isCopec) => {
        this.isCopec = isCopec;
      });

    this.timeTickerId = window.setInterval(
      () => this.updateCurrentTimeLeft(),
      CURRENT_TIME_REFRESH_MS
    );
  }

  ngAfterViewInit(): void {
    this.adjustHeights();
    this.updateRangeWidth();
  }

  ngAfterViewChecked(): void {
    if (this.ganttData.length && this.currentTimeNeedsScrollCenter) {
      const container = this.father?.nativeElement;
      if (container) {
        const scrollPosition = this.getCurrentTimeScrollingWidth();
        const targetScroll = Math.max(
          0,
          scrollPosition - container.offsetWidth / 2
        );
        container.scrollLeft = targetScroll;
        this.currentTimeNeedsScrollCenter = false;
      }
    }
    this.adjustHeights();
  }

  ngOnDestroy(): void {
    if (this.timeTickerId) window.clearInterval(this.timeTickerId);
    this.servicesSvc.ganttDataParams = {
      zoneId: this.selectedZone || '',
      searchTerm: this.searchTerm,
      date: this.selectedDate,
    };
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateRangeWidth();
    this.adjustHeights();
  }

  private showLoader(): void {
    if (this.loader) this.renderer.setStyle(this.loader, 'display', 'flex');
  }
  private hideLoader(): void {
    if (this.loader) this.renderer.setStyle(this.loader, 'display', 'none');
  }

  private loadGant(): void {
    this.getZones();
    this.getVerticals();
    this.restoreFromSession();
  }

  private async restoreFromSession(): Promise<void> {
    const savedDateRaw = this.sessionStorageService.get<string>(
      SessionStorageKeys.SELECTEDDATE
    );
    const savedZone = this.sessionStorageService.get<string>(
      SessionStorageKeys.SELECTEDZONE
    );

    if (!savedZone || !savedDateRaw) return;

    const savedDate = new Date(savedDateRaw);
    if (isNaN(savedDate.getTime())) {
      this.sessionStorageService.remove(SessionStorageKeys.SELECTEDDATE);
      return;
    }

    this.selectedZone = savedZone;
    this.selectedDate = savedDate;
    this.loadingGantt = true;
    try {
      await Promise.all([this.refreshGantt(), this.loadNoProviderServices()]);
    } finally {
      this.loadingGantt = false;
      this.isLoading = false;
      this.hideLoader();
    }
  }

  // ---------- Data fetching ----------
  getZones(): void {
    this.servicesSvc.getZones().subscribe((res: ZonesResponse) => {
      this.zones = res.zones;
    });
    this.selectedDate = new Date();
  }

  getVerticals(): void {
    this.verticals = this.servicesSvc.verticals;
  }

  async loadNoProviderServices(): Promise<void> {
    if (!this.selectedZone || !this.selectedDate) return;

    const d = new Date(this.selectedDate);
    const utcMidnight = Date.UTC(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      0,
      0,
      0
    );
    const seconds = Math.floor(utcMidnight / 1000);

    const payload: NoProviderServiceGetPayload = {
      zoneGuid: this.selectedZone,
      date: { seconds: String(seconds), nanos: 0 },
    };

    try {
      const data: NoProviderServiceGetResponse = await firstValueFrom(
        this.servicesSvc.getNoProviderSerivces(payload)
      );
      this.NoProviderServices = data?.servicesWithoutProvider ?? [];
    } catch (e) {
      console.error('Error al cargar servicios sin proveedor:', e);
    }
  }

  public async refreshGantt(
    zoneId?: string | null,
    date?: Date
  ): Promise<void> {
    this.isLoading = true;
    this.loadingGantt = true;

    const z = zoneId ?? this.selectedZone;
    const d = date ?? (this.selectedDate as Date);

    if (!z || !(d instanceof Date) || isNaN(d.getTime())) {
      console.warn('refreshGantt: zona o fecha inválida');
      this.isLoading = this.loadingGantt = false;
      return;
    }

    // Persistencia en sessionStorage
    this.sessionStorageService.set(SessionStorageKeys.SELECTEDZONE, z);
    this.sessionStorageService.set(
      SessionStorageKeys.SELECTEDDATE,
      d.toISOString()
    );

    this.showLoader();

    try {
      const unix = this.dateToUnix(
        `${String(d.getDate()).padStart(2, '0')}/${String(
          d.getMonth() + 1
        ).padStart(2, '0')}/${d.getFullYear()}`
      );

      const req: GanttRequest = {
        zoneId: z,
        date: { seconds: unix },
        buffer_amount: 10,
        isInternalEmployee: this.selectedIsInternalEmployee,
        driverType: this.selectedDriverType,
      };
      const { ganttData = [] } = await firstValueFrom(
        this.servicesSvc.getGantt(req)
      );

      ganttData.forEach((n) => (n.value.data = this.dedupeTasks(n.value.data)));
      this.fixTimes(ganttData);
      
      await this.attachDrivers(ganttData);

      ganttData.sort((a, b) =>
        (a.driver?.[0]?.fullName || '').localeCompare(
          b.driver?.[0]?.fullName || '',
          undefined,
          {
            sensitivity: 'base',
          }
        )
      );

      const unassignedLabel = this.translate.instant(
        'service_manager.gantt.unassigned_driver'
      );
      const unassignedRows = ganttData.filter(
        (r) => r.driver?.[0]?.fullName === unassignedLabel
      );
      const assignedRows = ganttData.filter(
        (r) => r.driver?.[0]?.fullName !== unassignedLabel
      );

      this.ganttData = [...assignedRows, ...unassignedRows];
      this.ganttDataOriginal = [...this.ganttData];

      this.searchTerm = '';
      this.updateOperatorTypes();
      this.updateTimelineFromData();

      await this.loadNoProviderServices();

      this.scrollToInitialHour();
      this.updateCurrentTimeLeft();
    } catch (err) {
      console.error('Error fetching Gantt', err);
      this.resetGanttState();
    } finally {
      this.hideLoader();
      this.loadingGantt = false;
      this.isLoading = false;
    }
  }

  // ---------- UI actions ----------
  onZoneChange(): void {
    this.refreshGantt(this.selectedZone, this.selectedDate as Date);
  }

  onDateChange(): void {
    if (!this.selectedDate) return;
    this.refreshGantt(this.selectedZone, this.selectedDate as Date);
  }

  onOperatorEmployeeChange(): void {
    this.refreshGantt(this.selectedZone, this.selectedDate as Date);
  }

  onDriverTypeChange(): void {
    this.refreshGantt(this.selectedZone, this.selectedDate as Date);
  }



  selectToday(): void {
    this.selectedDate = new Date();
    this.refreshGantt(this.selectedZone, this.selectedDate);
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    this.updateRangeWidth();
    this.updateHours();
    this.timelineWidth = this.computeTimelineWidth();
    this.updateCurrentTimeLeft();
  }

  handleToggleFullscreen(): void {
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement;
    });
  }

  openNoProvidersSide(): void {
    const config: NgbOffcanvasOptions = {
      backdrop: true,
      backdropClass: 'custom-backdrop',
      position: 'end',
      panelClass: 'custom-offcanvas-width-no-providers-side',
    };
    const offcanvasRef = this.offcanvasService.open(
      ServicesWithoutProviderSideComponent,
      config
    );

    const instance = offcanvasRef.componentInstance;
    instance.NoProviderServices = this.NoProviderServices;
    instance.ZoneId = this.selectedZone;

    instance.noProviderServicesChange.subscribe(
      (updated: NoProviderService[]) => {
        this.NoProviderServices = updated;
        this.loadNoProviderServices();
      }
    );
  }

  openSidebar(task: GanttTask): void {
    const originNodeId = this.getOriginNodeId(task);
    const isService = task.nodeType === 'OPERATIVE_NODE';

    const ref = this.offcanvasService.open(GanttSidebarComponent, {
      backdrop: true,
      position: 'end',
      keyboard: true,
      panelClass: 'gantt-sidebar-width w-443',
    });

    Object.assign(ref.componentInstance, {
      service: task,
      zoneId: this.selectedZone,
      zones: this.zones,
      dateService: this.selectedDate,
      originNodeId,
      isService,
    });

    ref.componentInstance.updateGantt.subscribe(() => this.refreshGantt());
    ref.componentInstance.closed.subscribe(() => ref.close());
    
    const ganttComponent = this;
    Object.defineProperty(ref.componentInstance, 'isGanttLoading', {
      get: () => ganttComponent.loadingGantt,
      enumerable: true,
      configurable: true,
    });
  }

  // ---------- Rendering helpers ----------
  private currentTimeNeedsScrollCenter = false;
  private readonly INITIAL_VIEW_HOUR = 9; // Vista inicial: 09:00 AM

  private computeTimelineWidth(): number {
    const container = this.father?.nativeElement;
    if (container) {
      const availableWidth = container.offsetWidth - 150; 
      return Math.max(availableWidth, 0);
    }
    return 2000;
  }

  private minuteToPx(): number {
    const intervalMinutes = this.halfHourMode ? 30 : 60;
    return this.rangeWidth / intervalMinutes;
  }

  private updateCurrentTimeLeft(): void {
    const now = new Date();
    const minutesFromStart =
      (now.getHours() - this.ganttStartTime) * 60 +
      now.getMinutes() +
      now.getSeconds() / 60;
    const px = minutesFromStart * this.minuteToPx();
    this.currentLeftPx = this.clamp(px, 0, this.timelineWidth);
  }

  private toSeconds(t?: TimeData | null): number {
    if (!t || t.seconds == null) return NaN;
    return typeof t.seconds === 'string' ? Number(t.seconds) : t.seconds;
  }

  private scrollToInitialHour(): void {
    const container = this.father?.nativeElement;
    if (!container) return;

    const minutesFromStart =
      (this.INITIAL_VIEW_HOUR - this.ganttStartTime) * 60;
    const px = Math.max(0, minutesFromStart * this.minuteToPx());
    const target = this.clamp(px, 0, this.timelineWidth);

    requestAnimationFrame(() => {
      container.scrollLeft = target;
    });
  }

  getCurrentTimeScrollingWidth(): number {
    const now = new Date();
    const minutesFromStart =
      (now.getHours() - this.ganttStartTime) * 60 +
      now.getMinutes() +
      now.getSeconds() / 60;
    const px = minutesFromStart * this.minuteToPx();
    return this.clamp(px, 0, this.timelineWidth);
  }

  updateRangeWidth(): void {
    this.timelineWidth = this.computeTimelineWidth();
    
    const totalHours = this.ganttEndTime - this.ganttStartTime + 1;
    const slotsPerHour = this.halfHourMode ? 2 : 1;
    const totalSlots = totalHours * slotsPerHour;
    
    this.rangeWidth = Math.floor(this.timelineWidth / totalSlots);
    
    this.updateCurrentTimeLeft();
  }

  updateHours(): void {
    this.hours = this.getHoursOfDay(
      this.ganttStartTime,
      this.ganttEndTime,
      this.halfHourMode
    );
  }

  onIntervalChange(option: { labelKey: string; value: boolean }): void {
    this.halfHourMode = option.value;
    this.updateRangeWidth();
    this.updateHours();
    this.updateCurrentTimeLeft();
  }

  trackByRow = (_: number, row: GanttDataItem) => row.key;
  trackByTask = (_: number, task: GanttTask) => task.nodeId;

  getHoursOfDay(
    startHour: number,
    endHour: number,
    halfHourMode: boolean
  ): string[] {
    const times: string[] = [];
    for (let i = startHour; i <= endHour; i++) {
      const hour = i < 10 ? `0${i}` : String(i);
      times.push(`${hour}:00`);
      if (halfHourMode) times.push(`${hour}:30`);
    }
    return times;
  }

  adjustHeights(): void {
    const div1Elements = this.driverArray.toArray();
    const div2Elements = this.servicesArray.toArray();
    div2Elements.forEach((servicesArray, index) => {
      const div1 = div1Elements[index];
      const h = servicesArray.nativeElement.offsetHeight;
      this.renderer.setStyle(div1.nativeElement, 'height', `${h}px`);
    });
  }

  getRowHeight(row: GanttDataItem): string {
    const tasks = this.getFlattenedTasks(row.value.data);
    const isSweeper = this.isSweeperDriver(row);
    if (!tasks.length) return isSweeper ? '100px' : '90px';

    tasks.forEach((t) => this.taskTier.delete(t));

    const endByTier: number[] = [];
    [...tasks]
      .sort(
        (a, b) =>
          this.convertToHourFormat(a.serviceStartDate) -
          this.convertToHourFormat(b.serviceStartDate)
      )
      .forEach((task) => {
        const start = this.convertToHourFormat(task.serviceStartDate);
        let tier = endByTier.findIndex((e) => start >= e);
        if (tier === -1) tier = endByTier.length;
        endByTier[tier] = this.convertToHourFormat(task.serviceEndDate);
        this.taskTier.set(task, tier);
      });

    const tiers = endByTier.length;
    const calculated = tiers * NODE_H + (tiers - 1) * GAP_Y;
    return isSweeper ? `${Math.max(calculated, 100)}px` : `${calculated}px`;
  }

  getPositionStyle(task: GanttTask): { [k: string]: string } {
    const startSec = this.toSeconds(task.serviceStartDate);
    const endSec = this.toSeconds(task.serviceEndDate);

    if (
      !Number.isFinite(startSec) ||
      !Number.isFinite(endSec) ||
      endSec <= startSec
    ) {
      return { display: 'none' };
    }

    const dayStartSec = this.ganttStartTime * 3600;
    const dayEndSec = (this.ganttEndTime + 1) * 3600;

    let s = (startSec - dayStartSec) / 60;
    let e = (endSec - dayStartSec) / 60;

    const minVisMin = 0;
    const maxVisMin = (this.ganttEndTime - this.ganttStartTime + 1) * 60;
    if (e <= minVisMin || s >= maxVisMin) return { display: 'none' };

    s = Math.max(s, minVisMin);
    e = Math.min(e, maxVisMin);

    const pxPerMin = this.minuteToPx();
    const left = s * pxPerMin;
    const width = Math.max((e - s) * pxPerMin, 1);

    const tier = this.taskTier.get(task) ?? 0;

    return {
      left: `${left}px`,
      width: `${width}px`,
      minWidth: `${width}px`,
      top: `${tier * (NODE_H + GAP_Y)}px`,
    };
  }

  getStatusBlock(block: boolean): string {
    return block ? 'gantt-task-block' : '';
  }

  getStatusStyle(task: GanttTask, type?: string): string {
    if (!task) return 'gantt-task-confirmed';

    if (type && type !== 'OPERATIVE_NODE') {
      switch (type) {
        case 'TRANSPORT_NODE':
        case 'VEHICLE_NODE':
          return 'gantt-task-public';
        case 'SWEEPER_NODE':
          return 'gantt-task-sweeper';
        default:
          return 'gantt-task-pending';
      }
    }

    const status = String(task.orderStatus).toUpperCase();
    switch (status) {
      case 'PENDING':
        return 'gantt-task-pending';
      case 'CONFIRMED':
        return 'gantt-task-confirmed';
      case 'DELIVERING':
      case 'IN_PROGRESS':
      case 'PICKING_UP':
      case '4':
      case '5':
        return 'gantt-task-inProgress';
      case 'FINISHED':
        return 'gantt-task-finished';
      case 'FAILED':
      case 'CANCELLED_BY_CAFLER':
      case 'CANCELLED_BY_CLIENT':
        return 'gantt-task-cancelled';
      default:
        return 'gantt-task-confirmed';
    }
  }

  filterData(): void {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.noMatches = false;
      this.ganttData = [...this.ganttDataOriginal];
      this.updateTimelineFromData();
      return;
    }

    const matches = this.ganttDataOriginal.filter(({ driver }) =>
      (driver?.[0]?.fullName ?? '').toLowerCase().includes(term)
    );

    this.noMatches = matches.length === 0;
    this.ganttData = matches;
    this.updateTimelineFromData();
  }

  /** Usada por la plantilla: [src]="getIcon(task.verticalType, task.nodeType)" */
  public getIcon(verticalType?: string, nodeType?: string): string {
    // Para nodos no operativos (transport/sweeper/etc.)
    if (nodeType && nodeType !== 'OPERATIVE_NODE') {
      const file = this.NODE_ICON_BY_TYPE[nodeType] ?? this.DEFAULT_ICON;
      return this.ICON_BASE_PATH + file;
    }

    // Operative node: icono por vertical
    if (!verticalType) return this.ICON_BASE_PATH + this.DEFAULT_ICON;

    const key = verticalType.toUpperCase();
    const file = this.OPERATIVE_ICON_BY_VERTICAL[key] ?? this.DEFAULT_ICON;
    return this.ICON_BASE_PATH + file;
  }

  getChassisLabel(code: string): string {
    const entry = vehicleChasisTypeWithTranslations.find(
      (c) => c.name.toLocaleUpperCase() === code.toLocaleUpperCase()
    );
    return entry
      ? this.translate.instant(`common.vehicleChassis.${entry.name}`)
      : code;
  }

  getProductTypeLabel(productType?: string): string {
    if (!productType) return '-';
    try {
      const translated = this.translate.instant(`PRODUCT_NAMES.${productType}`);
      return translated !== `PRODUCT_NAMES.${productType}`
        ? translated
        : productType;
    } catch {
      return '-';
    }
  }

  get currentLang(): string {
    return this.translate.currentLang || 'es';
  }

  // ---------- Internals ----------
  private convertToHourFormat(time?: TimeData | null): number {
    if (!time || time.seconds == null) return NaN;
    const raw =
      typeof time.seconds === 'string' ? Number(time.seconds) : time.seconds;
    return isNaN(raw) ? NaN : raw / 3600; // precisión total, sin redondeos
  }

  private dateToUnix(dateString: string): number {
    return moment.utc(dateString, 'DD/MM/YYYY').startOf('day').unix();
  }

  private resetGanttState(): void {
    this.ganttData = [];
    this.ganttDataOriginal = [];
    this.NoProviderServices = [];
    this.currentTimeNeedsScrollCenter = true;
    this.searchTerm = '';
    this.updateTimelineFromData();
  }

  private dedupeTasks(tasks: readonly GanttTask[]): GanttTask[] {
    const seen = new Set<string>();
    return tasks.filter((t) => {
      const key = [
        t.nodeId,
        t.nodeType,
        t.serviceStartDate.seconds,
        t.serviceEndDate.seconds,
      ].join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private async attachDrivers(data: GanttDataItem[]): Promise<void> {
    const defaultDriver: DriverGant = {
      fullName: this.translate.instant(
        'service_manager.gantt.unassigned_driver'
      ),
      operatorType: this.translate.instant('service_manager.gantt.no_type'),
      isActive: false,
      creationDate: { seconds: '0', nanos: 0 },
      additionalContactPhoneNumber: '',
      additionalEmailAddress: '',
      allowedMotorcycleServiceType: '',
      contactPhoneNumber: '',
      driverId: '',
      emailAddress: '',
      firstName: '',
      lastName: '',
      isInternal: false,
      profilePictureUrl: '',
      startAddress: '',
      startAddressDetail: '',
      startAddressPoint: { Longitude: 0, Latitude: 0, Srid: 0, _Srid: '' },
      workdayEndTime: { seconds: '0', nanos: 0 },
      workdayStartTime: { seconds: '0', nanos: 0 },
      secondShiftWorkdayStartTime: null,
      secondShiftWorkdayEndTime: null,
      zoneId: '',
    };

    const zoneId = this.selectedZone || '';
    const selectedDate = this.selectedDate ?? new Date();
    const formattedDate = this.formatDateAsDayMonthYear(selectedDate);

    await Promise.all(
      data.map(async (node) => {
        const drvRes = await firstValueFrom(
          this.servicesSvc.getGanttDriver(node.key, zoneId, formattedDate)
        );
        node.driver = drvRes.drivers?.length ? drvRes.drivers : [defaultDriver];

        node.value.data.forEach((t) => {
          t.tranformDateStart = formatSecondsToHourAndMinutes(
            t.serviceStartDate.seconds
          );
          t.tranformDateEnd = formatSecondsToHourAndMinutes(
            t.serviceEndDate.seconds
          );
        });
      })
    );
  }

  private getOriginNodeId(task: GanttTask): string | undefined {
    const row = this.ganttData.find((r) =>
      this.getFlattenedTasks(r.value.data).some((t) => t.nodeId === task.nodeId)
    );
    if (!row) return undefined;

    const tasks = this.getFlattenedTasks(row.value.data)
      .slice()
      .sort(
        (a, b) => +a.serviceStartDate.seconds - +b.serviceStartDate.seconds
      );

    const idx = tasks.findIndex((t) => t.nodeId === task.nodeId);
    return idx > 0 ? tasks[idx - 1].nodeId : undefined;
  }

  getFlattenedTasks(group: GanttTask[] | GanttTask[][]): GanttTask[] {
    if (!group.length) return [];
    return Array.isArray(group[0])
      ? (group as GanttTask[][]).flat()
      : (group as GanttTask[]);
  }

  private updateAllTaskStyles(): void {
    for (const item of this.ganttData) {
      const tasks = item.value?.data;
      for (const task of tasks) this.getPositionStyle(task);
    }
  }

  public getFormattedTime(seconds?: string | number | null): string {
    if (seconds === null || seconds === undefined) return '-';
    const s = typeof seconds === 'number' ? String(seconds) : seconds;
    return formatSecondsToHourAndMinutes(s);
  }

  public hasSecondShift(driver: DriverGant): boolean {
    return (
      this.hasTimeValue(driver.secondShiftWorkdayStartTime) &&
      this.hasTimeValue(driver.secondShiftWorkdayEndTime)
    );
  }

  private hasTimeValue(time?: TimeData | null): boolean {
    return (
      time !== null &&
      time !== undefined &&
      time.seconds !== null &&
      time.seconds !== undefined &&
      String(time.seconds).trim() !== ''
    );
  }

  private formatDateAsDayMonthYear(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  public getDriverTypeTranslationKey(operatorType: string | null | undefined): string | null {
    if (!operatorType) return null;
    return DriverTypeApiLabel[operatorType as DriverTypeApiValue] ?? null;
  }

  public getMobilityServiceTypeLabelKey(
    serviceTypeMobility: ServiceTypeMobility | string | null | undefined
  ): string | null {
    if (!serviceTypeMobility) return null;
    return (
      ServiceTypeMobilityLabelKey[
        serviceTypeMobility as ServiceTypeMobility
      ] ?? null
    );
  }

  public isSweeperDriver(row: GanttDataItem): boolean {
    return this.ganttDataOriginal.some((r) =>
      this.getFlattenedTasks(r.value.data).some((t) => t.sweeperDriverId === row.key)
    );
  }

  public onDownloadRoute(driver: DriverGant, row: GanttDataItem): void {
    if (this.downloadingRows.has(row.key)) return;

    const zoneId = this.selectedZone || '';
    const date = this.selectedDate ?? new Date();
    const t = (key: string) => this.translate.instant(`service_manager.gantt.${key}`);

    const sweeperDriverId = row.key;
    if (!sweeperDriverId) {
      console.error('No sweeperDriverId for row:', row.key);
      return;
    }
    this.downloadingRows.add(row.key);
    const unix = this.dateToUnix(
      `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
    );
    this.servicesSvc.getSweeperDriverRoute(sweeperDriverId, zoneId, unix).subscribe({
      next: (response) => {
        const dateLabel = moment(date).format('YYYY-MM-DD');
        const formatTimestampToTime = (timestamp: { seconds?: string | number; nanos?: number } | null | undefined) => {
          const unix = Number(timestamp?.seconds);
          if (isNaN(unix)) return '';
          return moment.unix(unix).tz(moment.tz.guess()).format('HH:mm');
        };

        this.excelSvc.download({
          fileName: `${t('excel_file_name')}_${response.fullName.replace(/\s+/g, '_')}_${dateLabel}.xlsx`,
          sheets: [{
            sheetName: t('excel_sheet_name'),
            headerBlock: [
              { label: t('excel_sweeper_driver'), value: response.fullName },
              { label: t('excel_phone'),          value: response.phoneNumber },
              { label: t('excel_date'),           value: dateLabel },
            ],
            columns: [
              { header: t('excel_col_assigned_driver'), width: 25 },
              { header: t('excel_col_driver_phone'),    width: 20 },
              { header: t('excel_col_origin_address'),  width: 50 },
              { header: t('excel_col_dest_address'),    width: 50 },
              { header: t('excel_col_start_time'),      width: 12 },
              { header: t('excel_col_end_time'),        width: 12 },
            ],
            rows: response.routeNodes.map((n) => [
              n.driverFullName,
              n.driverPhoneNumber,
              n.startAddress,
              n.endAddress,
              formatTimestampToTime(n.startDate),
              formatTimestampToTime(n.endDate),
            ]),
          }],
        });
        this.downloadingRows.delete(row.key);
        this.alerts.open(t('excel_download_success'), {
          label: t('excel_download_success_label'),
          status: 'success',
          autoClose: true,
        }).subscribe();
      },
      error: (err) => {
        this.downloadingRows.delete(row.key);
        console.error('Error descargando ruta escoba:', err);
        this.alerts.open(t('excel_download_error'), {
          label: t('excel_download_error_label'),
          status: 'error',
          autoClose: true,
        }).subscribe();
      },
    });
  }

  private updateOperatorTypes(): void {
    this.operatorTypes = Array.from(
      new Set(
        this.ganttDataOriginal
          .flatMap((n) => n.driver?.map((d) => d.operatorType))
          .filter(Boolean)
      )
    ).map((type) => ({
      label: this.translate.instant(`operatorType.${type}`),
      value: type,
    }));
  }

  filterByOperatorType(): void {
    if (!this.selectedOperatorType) {
      this.ganttData = [...this.ganttDataOriginal];
      this.updateTimelineFromData();
      return;
    }
    this.ganttData = this.ganttDataOriginal.filter((node) =>
      node.driver?.some(
        (driver) => driver.operatorType === this.selectedOperatorType
      )
    );
    this.updateTimelineFromData();
  }

  private fixTimes(data: GanttDataItem[]): void {
    data.forEach((node) => {
      node.value.data.forEach((t: GanttTask) => {
        const ini = +t.serviceStartDate.seconds;
        const end = +t.serviceEndDate.seconds;

        if (end - ini <= 0) {
          const realDur = /^\d+h\s+\d+min$/.test(t.totalTime ?? '')
            ? (+t.totalTime.split('h')[0] * 60 +
                +t.totalTime.split('min')[0].split(' ').pop()!) *
              60
            : DEF_SEC;

          t.serviceEndDate.seconds = String(ini + Math.max(realDur, MIN_SEC));
          t.totalTime =
            Math.round((+t.serviceEndDate.seconds - ini) / 60) + 'min';
        }
      });
    });
  }

  private clamp(n: number, min: number, max: number): number {
    return Math.max(min, Math.min(n, max));
  }

  get hasAnyTasks(): boolean {
    return this.ganttData.some(
      (row) => this.getFlattenedTasks(row.value?.data ?? []).length > 0
    );
  }

  private updateTimelineFromData(): void {
    this.ganttStartTime = GANTT_START_H;
    this.ganttEndTime = GANTT_END_H;
    this.updateHours();
    this.updateCurrentTimeLeft();
    this.scrollToFirstService();
  }

  private scrollToFirstService(): void {
    const container = this.father?.nativeElement;
    if (!container) return;

    const tasks = this.ganttData.flatMap((row) =>
      this.getFlattenedTasks(row.value?.data ?? [])
    );

    if (!tasks.length) return;

    const { minHour, maxHour } = this.getServiceHourRange(tasks);
    if (minHour === -1 || maxHour === -1) return;

    const horizontalScroll = this.calculateHorizontalScroll(minHour, maxHour, container);
    const firstRowIndex = this.getFirstRowWithServices();

    setTimeout(() => {
      container.scrollLeft = horizontalScroll;
      
      if (firstRowIndex !== -1) {
        const verticalScroll = this.calculateVerticalScroll(firstRowIndex, container);
        container.scrollTop = verticalScroll;
      }
    }, 100);
  }

  private getServiceHourRange(tasks: GanttTask[]): { minHour: number; maxHour: number } {
    const starts = tasks
      .map((t) => this.toSeconds(t.serviceStartDate))
      .filter((s) => Number.isFinite(s)) as number[];
    const ends = tasks
      .map((t) => this.toSeconds(t.serviceEndDate))
      .filter((s) => Number.isFinite(s)) as number[];

    if (!starts.length || !ends.length) return { minHour: -1, maxHour: -1 };

    const minStart = Math.min(...starts);
    const maxEnd = Math.max(...ends);

    return {
      minHour: Math.floor(minStart / 3600),
      maxHour: Math.ceil(maxEnd / 3600)
    };
  }

  private calculateHorizontalScroll(minHour: number, maxHour: number, container: HTMLElement): number {
    const midHour = (minHour + maxHour) / 2;
    const minutesFromStart = (midHour - this.ganttStartTime) * 60;
    const px = minutesFromStart * this.minuteToPx();
    const centerOffset = container.offsetWidth / 2;
    return Math.max(0, px - centerOffset);
  }

  private getFirstRowWithServices(): number {
    return this.ganttData.findIndex(
      (row) => this.getFlattenedTasks(row.value?.data ?? []).length > 0
    );
  }

  private calculateVerticalScroll(rowIndex: number, container: HTMLElement): number {
    const rowElements = this.servicesArray?.toArray();
    if (!rowElements || !rowElements[rowIndex]) return 0;

    const targetRow = rowElements[rowIndex];
    const rowTop = targetRow.nativeElement.offsetTop;
    const containerHeight = container.offsetHeight;
    const rowHeight = targetRow.nativeElement.offsetHeight;
    const verticalCenter = rowTop - (containerHeight / 2) + (rowHeight / 2);
    
    return Math.max(0, verticalCenter);
  }
}
